// The issue canvas showing a DOCUMENT.
//
// Pasting a PDF or a spreadsheet onto an issue used to put a chip in the
// attachments strip and nothing else: to read the thing you left the
// page. The canvas is where the issue's evidence belongs, so a document
// opens here — a PDF rendered page by page with pdf.js (the same build
// Document Control uses), a spreadsheet parsed into a table with the
// xlsx / papaparse the app already ships, and anything else offered
// honestly as a download rather than pretended at.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Select, Spin, Tooltip, message } from "antd";
import {
  LeftOutlined, RightOutlined, ZoomInOutlined, ZoomOutOutlined,
  DownloadOutlined, ExportOutlined, FilePdfOutlined, FileExcelOutlined, FileOutlined,
} from "@ant-design/icons";
import { loadPdfJs, resolveDocumentUrl, absoluteDocumentUrl } from "helpers/pdfPreview";
import { T } from "pages/Issue/theme";
import { useWheelZoom } from "helpers/wheelZoom";

// Browsers refuse to paint a canvas past roughly 2^28 pixels; stay well under.
const MAX_CANVAS_PIXELS = 24e6;

const isPdf = (a) => /pdf/i.test(a?.kind || "") || /\.pdf(\.gz)?$/i.test(a?.url || a?.name || "");
const isSheet = (a) => /excel|csv|spreadsheet/i.test(a?.kind || "") || /\.(xlsx?|xlsm|csv)$/i.test(a?.url || a?.name || "");

// ── PDF ────────────────────────────────────────────────────────────────
function PdfView({ doc }) {
  const scrollRef = useRef(null);
  const canvasRef = useRef(null);
  const pdfRef = useRef(null);
  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(1);
  const [zoom, setZoom] = useState(1);
  // The wheel zooms the page, like every other canvas in the app
  // (helpers/wheelZoom). Attached natively so it can preventDefault -
  // otherwise the scroller just scrolls and the drawing never scales.
  useWheelZoom(scrollRef, (factor) => {
    setZoom((z) => Math.max(0.25, Math.min(6, z * factor)));
  }, []);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let alive = true;
    setBusy(true); setErr(""); setPages(0); setPage(1);
    (async () => {
      try {
        const [pdfjs, src] = await Promise.all([loadPdfJs(), resolveDocumentUrl(doc.url)]);
        const task = pdfjs.getDocument(src);
        const pdf = await task.promise;
        if (!alive) return;
        pdfRef.current = pdf;
        setPages(pdf.numPages);
      } catch (e) {
        if (alive) setErr(e?.message || "This PDF could not be opened");
      } finally {
        if (alive) setBusy(false);
      }
    })();
    return () => { alive = false; pdfRef.current = null; };
  }, [doc.url]);

  // ONE PAINT AT A TIME, ON A CANVAS THAT PAINT OWNS.
  //
  // pdf.js refuses a canvas that is already being painted - "Cannot use
  // the same canvas during multiple render() operations" - and
  // cancelling the task in flight is NOT enough to make sharing one
  // canvas safe: pdf.js claims the canvas asynchronously, after
  // render() has already returned, so a wheel gesture that starts the
  // next draw before the previous one has reached that point still puts
  // two renders on one canvas. Button-click zoom was too slow to
  // collide; the wheel is not, which is why this only appeared once the
  // wheel could drive the zoom.
  //
  // So each paint renders onto its OWN offscreen canvas and blits the
  // finished bitmap onto the visible one - two paints cannot collide by
  // construction, whatever pdf.js does internally. And paints do not
  // stack: while one is running the next is held as "pending", latest
  // wins, so a long wheel gesture ends in one render rather than one
  // per tick.
  const taskRef = useRef(null);
  const drawSeq = useRef(0);
  const runningRef = useRef(false);
  const pendingRef = useRef(false);
  // The paint loop reads the CURRENT page/zoom rather than the ones it
  // was queued with, so a queued paint is never a stale one.
  const paramsRef = useRef({ page: 1, zoom: 1 });
  paramsRef.current = { page, zoom };

  const paint = useCallback(async () => {
    const pdf = pdfRef.current;
    const canvas = canvasRef.current;
    if (!pdf || !canvas) return;
    const seq = ++drawSeq.current;
    const { page: wantPage, zoom: wantZoom } = paramsRef.current;
    let off = null;
    try {
      const pg = await pdf.getPage(Math.min(Math.max(wantPage, 1), pdf.numPages));
      if (seq !== drawSeq.current) return;   // superseded while fetching
      const box = canvas.parentElement;
      const base = pg.getViewport({ scale: 1 });
      // Fit the width of the pane, then apply the user's zoom.
      const fit = box ? (box.clientWidth - 24) / base.width : 1;
      let scale = Math.max(0.1, fit * wantZoom);
      // A canvas has a hard size limit - browsers stop at around 2^28
      // pixels and then paint NOTHING, silently. A big sheet at 6x is
      // well past it, so the drawing is held to the budget instead of
      // vanishing at the zoom where it is most wanted.
      const wanted = base.width * base.height * scale * scale;
      if (wanted > MAX_CANVAS_PIXELS) scale *= Math.sqrt(MAX_CANVAS_PIXELS / wanted);
      const viewport = pg.getViewport({ scale });
      off = document.createElement("canvas");
      off.width = Math.max(1, Math.floor(viewport.width));
      off.height = Math.max(1, Math.floor(viewport.height));
      const task = pg.render({ canvasContext: off.getContext("2d"), viewport });
      taskRef.current = task;
      await task.promise;
      if (taskRef.current === task) taskRef.current = null;
      if (seq !== drawSeq.current) return;   // a newer paint won while this one drew
      canvas.width = off.width;
      canvas.height = off.height;
      canvas.getContext("2d").drawImage(off, 0, 0);
    } catch (e) {
      // The one error that means "working as intended".
      if (e && (e.name === "RenderingCancelledException" || /cancel/i.test(e.message || ""))) return;
      setErr(e?.message || "This page could not be rendered");
    } finally {
      // Let the offscreen bitmap go now rather than at the whim of the
      // collector - a zoomed A1 sheet is tens of megabytes, and the
      // wheel makes one of these per gesture.
      if (off) { off.width = 0; off.height = 0; }
    }
  }, []);

  const draw = useCallback(async () => {
    if (runningRef.current) {
      pendingRef.current = true;
      // Whatever is painting is already out of date. Stopping it is
      // what makes the wheel feel live instead of queued.
      if (taskRef.current) { try { taskRef.current.cancel(); } catch (e) { /* finished */ } }
      return;
    }
    runningRef.current = true;
    try {
      do {
        pendingRef.current = false;
        await paint();
      } while (pendingRef.current);
    } finally {
      runningRef.current = false;
    }
  }, [paint]);

  // Nothing should still be painting a canvas that is going away.
  useEffect(() => () => {
    drawSeq.current += 1;
    if (taskRef.current) { try { taskRef.current.cancel(); } catch (e) { /* gone */ } }
  }, []);

  useEffect(() => { if (pages) draw(); }, [pages, page, zoom, draw]);

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 8px", background: "#15181B", color: "#D7DDE2", fontSize: 12 }}>
        <FilePdfOutlined style={{ color: "#d64545" }} />
        <span style={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{doc.name || "Document"}</span>
        <span style={{ flex: 1 }} />
        <Button size="small" type="text" style={{ color: "#D7DDE2" }} icon={<LeftOutlined />} disabled={page <= 1} onClick={() => setPage((p) => p - 1)} />
        <span>{pages ? `${page} / ${pages}` : "…"}</span>
        <Button size="small" type="text" style={{ color: "#D7DDE2" }} icon={<RightOutlined />} disabled={page >= pages} onClick={() => setPage((p) => p + 1)} />
        <Button size="small" type="text" style={{ color: "#D7DDE2" }} icon={<ZoomOutOutlined />} onClick={() => setZoom((z) => Math.max(0.25, z - 0.25))} />
        <Button size="small" type="text" style={{ color: "#D7DDE2" }} icon={<ZoomInOutlined />} onClick={() => setZoom((z) => Math.min(4, z + 0.25))} />
        <Tooltip title="Open in a new tab">
          <Button size="small" type="text" style={{ color: "#D7DDE2" }} icon={<ExportOutlined />}
                  onClick={() => window.open(absoluteDocumentUrl(doc.url), "_blank", "noopener")} />
        </Tooltip>
      </div>
      <div ref={scrollRef} style={{ flex: 1, minHeight: 0, overflow: "auto", background: "#2A2E33", padding: 12, display: "flex", justifyContent: "center" }}>
        {busy && <Spin />}
        {err && <div style={{ color: "#FCA5A5", fontSize: 12, alignSelf: "center" }}>{err}</div>}
        <canvas ref={canvasRef} style={{ display: busy || err ? "none" : "block", maxWidth: "100%", background: "#fff", boxShadow: "0 2px 12px rgba(0,0,0,.4)" }} />
      </div>
    </div>
  );
}

// ── Spreadsheet ────────────────────────────────────────────────────────
function SheetView({ doc }) {
  const [rows, setRows] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [active, setActive] = useState("");
  const [err, setErr] = useState("");
  const bookRef = useRef(null);

  useEffect(() => {
    let alive = true;
    setRows(null); setErr(""); setSheets([]); setActive("");
    (async () => {
      try {
        const src = await resolveDocumentUrl(doc.url);
        const res = await fetch(src);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        if (/\.csv$/i.test(doc.url || doc.name || "")) {
          const text = await res.text();
          const Papa = (await import("papaparse")).default;
          const parsed = Papa.parse(text, { skipEmptyLines: true });
          if (!alive) return;
          setRows(parsed.data || []);
        } else {
          const buf = await res.arrayBuffer();
          const XLSX = await import("xlsx");
          const book = XLSX.read(buf, { type: "array" });
          if (!alive) return;
          bookRef.current = { XLSX, book };
          setSheets(book.SheetNames || []);
          setActive((book.SheetNames || [])[0] || "");
        }
      } catch (e) {
        if (alive) setErr(e?.message || "This file could not be read");
      }
    })();
    return () => { alive = false; };
  }, [doc.url, doc.name]);

  useEffect(() => {
    if (!active || !bookRef.current) return;
    const { XLSX, book } = bookRef.current;
    try {
      setRows(XLSX.utils.sheet_to_json(book.Sheets[active], { header: 1, blankrows: false, defval: "" }));
    } catch (e) {
      setErr(e?.message || "This tab could not be read");
    }
  }, [active]);

  const head = (rows || [])[0] || [];
  const body = (rows || []).slice(1, 500);

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", background: "#15181B", color: "#D7DDE2", fontSize: 12 }}>
        <FileExcelOutlined style={{ color: "#1f8f4e" }} />
        <span style={{ maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{doc.name || "Spreadsheet"}</span>
        {sheets.length > 1 && (
          <Select size="small" value={active} onChange={setActive} style={{ minWidth: 140 }}
                  options={sheets.map((s) => ({ value: s, label: s }))} />
        )}
        <span style={{ flex: 1 }} />
        {rows && <span style={{ color: "#7C8B96" }}>{rows.length} row{rows.length === 1 ? "" : "s"}{rows.length > 501 ? " (first 500 shown)" : ""}</span>}
        <Tooltip title="Download">
          <Button size="small" type="text" style={{ color: "#D7DDE2" }} icon={<DownloadOutlined />}
                  onClick={() => window.open(absoluteDocumentUrl(doc.url), "_blank", "noopener")} />
        </Tooltip>
      </div>
      <div style={{ flex: 1, minHeight: 0, overflow: "auto", background: "#fff" }}>
        {!rows && !err && <div style={{ padding: 20, textAlign: "center" }}><Spin /></div>}
        {err && <div style={{ padding: 20, color: T.removed2, fontSize: 12 }}>{err}</div>}
        {rows && (
          <table style={{ borderCollapse: "collapse", fontSize: 12, width: "100%" }}>
            <thead>
              <tr>
                {head.map((h, i) => (
                  <th key={i} style={{ position: "sticky", top: 0, background: "#F3F4F5", borderBottom: `1px solid ${T.line}`, padding: "5px 8px", textAlign: "left", whiteSpace: "nowrap" }}>
                    {String(h ?? "")}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {body.map((r, i) => (
                <tr key={i} style={{ background: i % 2 ? "#FAFBFC" : "#fff" }}>
                  {head.map((_, c) => (
                    <td key={c} style={{ borderBottom: `1px solid ${T.line2}`, padding: "4px 8px", whiteSpace: "nowrap" }}>
                      {String((r || [])[c] ?? "")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default function IssueDocumentPane({ docs, selectedUrl, onSelect, fill = true, height }) {
  const list = docs || [];
  const doc = useMemo(
    () => list.find((d) => d.url === selectedUrl) || list[0] || null,
    [list, selectedUrl]
  );
  const shell = {
    position: "relative", background: "#1A1A1A", border: "1px solid #0C0C0C",
    borderRadius: 6, overflow: "hidden", display: "flex", flexDirection: "column",
    ...(fill ? { flex: 1, minHeight: 0 } : { height: height || 420 }),
  };
  if (!doc) {
    return (
      <div style={{ ...shell, alignItems: "center", justifyContent: "center", color: "rgba(255,255,255,.6)", fontSize: 12 }}>
        No document attached — paste a PDF or a spreadsheet (Ctrl+V) and it opens here.
      </div>
    );
  }
  return (
    <div style={shell}>
      {isPdf(doc) ? <PdfView doc={doc} />
        : isSheet(doc) ? <SheetView doc={doc} />
        : (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, color: "rgba(255,255,255,.75)", fontSize: 12 }}>
            <FileOutlined style={{ fontSize: 28 }} />
            <div>{doc.name || "File"}</div>
            <div style={{ color: "rgba(255,255,255,.5)" }}>No viewer for this format — open it in a new tab.</div>
            <Button size="small" icon={<ExportOutlined />} onClick={() => window.open(absoluteDocumentUrl(doc.url), "_blank", "noopener")}>Open</Button>
          </div>
        )}
      {list.length > 1 && (
        <div style={{ display: "flex", gap: 6, padding: 8, background: "#15181B", overflowX: "auto" }}>
          {list.map((d) => (
            <button
              key={d.url}
              type="button"
              onClick={() => onSelect && onSelect(d.url)}
              title={d.name || d.url}
              style={{
                border: `1px solid ${d.url === doc.url ? T.navy : "#2A3036"}`,
                background: d.url === doc.url ? "#22303A" : "#1A1D21",
                color: "#D7DDE2", borderRadius: 4, fontSize: 11, padding: "3px 8px",
                cursor: "pointer", whiteSpace: "nowrap", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis",
              }}
            >
              {d.name || d.url.split("/").pop()}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
